import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import { colour } from './tokens';
import { allCommentsColumn, latestCommentColumn } from './commentColumns';
import { contactColumn, familyColumn } from './familyColumns';
import { askedForColumn, holdingColumn, journeyColumn } from './journeyColumns';
import { formatMoney } from '../../utils/money';
import { filterableHeader, YES_NO_OPTIONS } from './HeaderFilter';

/**
 * The columns the accounts team asked for, in the order they asked for them.
 *
 * This is a reconciliation view rather than a queue: AR read it against BPoint's own reports and
 * against TASS, so the BPoint references are here in full — receipt, merchant and settlement
 * date — and the names are shortened to fit fifteen columns on a screen rather than spelled out.
 *
 * Anything a family has no value for reads NA. Not a blank, which looks like a column that
 * failed to load, and not a zero or a dash, which read as answers. NA says we do not hold it —
 * which for a family who has not paid is the truthful answer about every BPoint field.
 */

const NA = 'NA';

const text = (value) => {
    const result = value == null ? '' : String(value).trim();
    return result === '' ? NA : result;
};

/** The database stores these as the words Yes and No, and sometimes as neither. */
const yesNo = (value) => {
    const word = String(value ?? '').trim().toLowerCase();
    if (word === 'yes' || word === '1' || word === 'true') return 'Yes';
    if (word === 'no' || word === '0' || word === 'false') return 'No';
    return NA;
};

/**
 * "Declined", where an amount would otherwise sit.
 *
 * The bank refused the payment and nothing was taken — but `paid_amount` holds what the family
 * ATTEMPTED and `remaining_amount` was left as though it had gone through. So a refused row reads as
 * a part-payment: the Tisekar family were turned down under 3-D Secure and their row says 6,255
 * paid, nothing remaining.
 *
 * Shown as a word rather than NA or a zero. NA means "we do not hold this", and a zero reads as an
 * answer; neither says the bank said no. It carries into the CSV too, because the figure AR would
 * otherwise lift into Power BI is money that never arrived.
 */
const Declined = () => (
    <Typography sx={{
        fontSize: '12.5px',
        fontWeight: 700,
        color: colour.dangerInk,
        whiteSpace: 'nowrap',
    }}>
        Declined
    </Typography>
);

const Cell = ({ value, dim }) => (
    <Typography sx={{
        fontSize: '13px',
        // NA is not the reading — it is the absence of one — so it stays out of the way.
        color: value === NA ? colour.faint : (dim ? colour.bodySoft : colour.body),
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    }}>
        {value}
    </Typography>
);

/**
 * A way of reaching the family, marked when the parent changed it.
 *
 * Chasing a fee means ringing somebody, so these are the accounts team's as much as enrolment's.
 * The mark matters more than it looks: without it somebody rings the number they already had and
 * wonders why nobody answers.
 *
 * The "(new)" rides in the value rather than in a chip so that it survives the CSV export — this
 * view gets lifted into Power BI and a colour does not travel.
 */
const contact = (field, headerName, width) => ({
    field,
    headerName,
    width,
    valueGetter: (params) => {
        const value = text(params.row[field]);
        if (value === NA) return NA;
        const isNew = (params.row.changed_fields || []).includes(field);
        return isNew ? `${value} (new)` : value;
    },
    renderCell: (params) => (
        <Cell value={params.value} dim={!String(params.value).includes('(new)')} />
    ),
});

const plain = (field, headerName, width, get) => ({
    field,
    headerName,
    width,
    valueGetter: (params) => get(params.row),
    renderCell: (params) => <Cell value={params.value} />,
});

/**
 * BPoint returns the settlement date as YYYYMMDD and it is stored exactly as given, so it is
 * shown split rather than parsed into a Date — a timezone has no business anywhere near a
 * banking date, and AR match this against BPoint's report character for character.
 */
export const settlementLabel = (value) => {
    const raw = String(value ?? '').trim();
    if (!raw) return NA;
    if (!/^\d{8}$/.test(raw)) return raw;

    return `${raw.slice(6, 8)}/${raw.slice(4, 6)}/${raw.slice(0, 4)}`;
};

/** "23/09/2026 14:05", from the two columns the browser wrote them into. */
export const transactionLabel = (row = {}) => {
    const date = String(row.payment?.date ?? '').trim();
    const time = String(row.payment?.time ?? '').trim();

    if (!date && !time) return NA;
    return [date, time].filter(Boolean).join(' ');
};

/**
 * A staff member, and which one.
 *
 * "Staff: Yes" beside "Staff ID: 1001023" said the same thing twice — the number is the answer
 * and the yes is implied by having one. Combined, the column is shorter and says more.
 *
 * Yes with no number is still possible in the data, so it is shown rather than being reported as
 * not staff at all.
 */
export const staffLabel = (row = {}) => {
    const id = String(row.staff_member_id ?? '').trim();
    if (id) return id;
    return yesNo(row.staff_member) === 'Yes' ? 'Yes' : 'No';
};

/**
 * The Health Care Card, and when it runs out.
 *
 * Same redundancy: a card with an expiry date is a card they have claimed, so the date carries
 * the yes. No date against a claimed card is shown as a claim without one, which is a thing AR
 * would want to see rather than have rounded to "yes".
 */
export const hccLabel = (row = {}) => {
    if (yesNo(row.health_care_card) !== 'Yes') return 'No';

    const expiry = String(row.hcc_expiry_date ?? '').trim();
    return expiry || 'Yes, no date';
};

/**
 * The columns for the accounts team.
 *
 * Compact by default: the pairs above are combined, and the three that are context rather than
 * reconciliation — whether the family spans campuses, whether a card picture was sent, and the
 * merchant number, which is the same for every row at a campus — move behind "All columns".
 *
 * Nothing is lost by the default, and the full set is one button away for an export or for the
 * row where the detail matters.
 */
/** A row's contact fields as entries for the Contact column, each marked if the parent just changed it. */
const newMarked = (row, fields) => fields
    .map((field) => ({ field, value: String(row[field] ?? '').trim() }))
    .filter((entry) => entry.value)
    .map((entry) => ({ value: entry.value, isNew: (row.changed_fields || []).includes(entry.field) }));

export const accountsColumns = ({
    showCampus = true, compact = true,
    filters = {}, onFilter, discountTypes = [],
} = {}) => {
    /**
     * Puts a filter on a heading, where the server can narrow by it.
     *
     * A column with no server-side filter behind it gets a plain heading rather than an icon
     * that opens a menu and changes nothing.
     */
    const filtered = (key, label, options = YES_NO_OPTIONS) => (onFilter
        ? {
            renderHeader: filterableHeader({
                label,
                value: filters[key] ?? '',
                options,
                onChange: (value) => onFilter({ ...filters, [key]: value }),
            }),
        }
        : {});

    return [
    // The family, parent code under the name, frozen while the rest scrolls: the same first column
    // as every staff table (familyColumns.js). Exported as the name alone; the code has its own
    // exported column below, so Power BI gets one value per field.
    familyColumn({ name: (row) => text(row.family_name), code: (row) => row.parent_id }),
    // Always defined, so the CSV keeps one value per field for Power BI and the grid can sort by
    // code. Hidden on screen while compact — the code is already under every family name — by
    // ApproverScreen's columnVisibilityModel, which is also why the export sets allColumns.
    plain('parent_id', 'Parent', 95, (row) => text(row.parent_id)),
    ...(showCampus ? [plain('college', 'Campus', 110, (row) => text(row.college))] : []),
    // Whether this family also has children somewhere else, which changes how their fees were
    // worked out and is the first thing AR check when a total looks wrong.
    ...(compact ? [] : [plain('multi_campus', 'Multi', 70, (row) => (
        row.campus_count == null ? NA : (Number(row.campus_count) > 1 ? 'Yes' : 'No')
    ))]),
    // Both parents, both ways, in one column, third as on every staff table: chasing a fee means
    // trying one number, then the other, then the emails. A parent's correction is taken at their
    // word: "(new)" says it is new, not that it needs checking.
    ...(compact
        ? [contactColumn({
            phones: (row) => newMarked(row, ['mobile1', 'mobile2']),
            emails: (row) => newMarked(row, ['e_mail', 'e_mail2']),
            minWidth: 190,
        })]
        : []),
    // Defined either way so the export carries each separately; hidden on screen while compact.
    contact('mobile1', 'Mobile', 135),
    contact('mobile2', 'Mobile 2', 135),
    contact('e_mail', 'Email', 200),
    contact('e_mail2', 'Email 2', 200),
    plain('no_of_childrens', 'Students', 80, (row) => text(row.no_of_childrens)),
    // Where the parent got to, what is holding them, and what the payment page would have asked
    // for. The money columns below say what was *paid*, which for a part-way family is nothing —
    // so on the in-progress view they were a row of dashes and these three are the whole story.
    journeyColumn(),
    holdingColumn(),
    askedForColumn(),
    {
        ...plain('receipting', 'Receipted', 110, (row) => (
            row.sync?.receipting === 'synced' ? 'Yes' : 'No'
        )),
        ...filtered('receipted', 'Receipted'),
    },
    ...(compact
        ? [{ ...plain('staff', 'Staff', 110, staffLabel), ...filtered('staff', 'Staff') }]
        : [
            { ...plain('staff_member', 'Staff', 90, (row) => yesNo(row.staff_member)), ...filtered('staff', 'Staff') },
            plain('staff_member_id', 'Staff ID', 90, (row) => text(row.staff_member_id)),
        ]),
    ...(compact
        ? [{
            field: 'hcc',
            headerName: 'HCC',
            flex: 0.6,
            minWidth: 130,
            ...filtered('hcc', 'HCC'),
            valueGetter: (params) => hccLabel(params.row),
            renderCell: (params) => {
                // A claim with no picture is the one AR have to chase, so the column that says
                // they claimed is also the column that says it is not evidenced yet.
                const claimed = params.value !== 'No';
                const sent = Number(params.row.hcc_image_count || 0) > 0;
                return (
                    <Cell value={claimed && !sent ? `${params.value} · no card` : params.value} />
                );
            },
        }]
        : [
            { ...plain('health_care_card', 'HCC', 90, (row) => yesNo(row.health_care_card)), ...filtered('hcc', 'HCC') },
            plain('hcc_expiry_date', 'HCC expiry', 110, (row) => text(row.hcc_expiry_date)),
            {
                ...plain('hcc_card_sent', 'Card sent', 115, (row) => {
                    if (yesNo(row.health_care_card) !== 'Yes') return NA;
                    return Number(row.hcc_image_count || 0) > 0 ? 'Yes' : 'No';
                }),
                ...filtered('cardSent', 'Card sent'),
            },
        ]),
    {
        // The KIND of discount — "Health Care Card", "Staff Discount". It was headed "Discount",
        // which now belongs to the amount in the money group: two columns called the same thing,
        // one a word and one a figure, is a column heading that answers neither question.
        ...plain('discount_type', 'Discount type', undefined, (row) => text(row.discount_type)),
        flex: 0.6,
        minWidth: 135,
        ...filtered('discount', 'Discount type', [
            { value: '', label: 'Any' },
            ...discountTypes.map((type) => ({ value: type, label: type })),
        ]),
    },
    /**
     * THE MONEY, IN THE ORDER IT HAPPENS. Then the bank references.
     *
     * The dates and the receipt used to sit here, between the discount and the figures, so the
     * money read: paid at, settled, receipt, fees, left, paid, card, credit. Two interleaved
     * stories, and the fee came after the receipt for it.
     *
     * Now it follows one: what the year costs, what covered it, what that came to, what is left,
     * and how the rest is to be settled —
     *
     *     Fees  ->  Credit  ->  Card  ->  Paid  ->  Left  ->  Pay rest by
     *
     * Credit and Card before Paid because Paid is their sum; Left after Paid because it is what
     * Paid did not cover. The references AR reconcile against — when it was paid, when BPoint
     * settled, the receipt number — follow as a group, because they answer a different question
     * from any of the amounts.
     */
    {
        /**
         * The year's fees — "Total Calculated Fees" as AR named it, shortened to sit beside the
         * other money columns.
         *
         * `payment.yearTotal` — school fees BEFORE discount, plus the resource fee. Before, so the
         * row reads as a sum that works out:
         *
         *     Fees  -  Discount  +  Debt  -  Paid  =  Left
         *
         * Verified on production: that holds for 319 of 323 paid rows.
         *
         * It read `payment.total` and was wrong twice over. `total` is
         * `pay_details.total_amount`, which holds what the family CHOSE to pay — parent 18576's
         * fees are 7,150 and their `total_amount` is 1,250, the minimum they picked. And the fee
         * AFTER discount would be deducted twice once Discount has a column of its own.
         */
        field: 'total_fees',
        headerName: 'Fees',
        width: 105,
        valueGetter: (params) => (params.row.payment?.paid ? params.row.payment.yearTotal : null),
        renderCell: (params) => (
            <Cell value={params.value == null ? NA : formatMoney(params.value)} />
        ),
    },
    {
        /**
         * The discount as an AMOUNT.
         *
         * The "Discount" column further back is the kind of discount — "Health Care Card", "Staff
         * Discount" — which answers why, not how much. AR asked for the figure, because a row that
         * starts from the fee before discount needs it to come out right.
         */
        field: 'discount_amount',
        headerName: 'Discount',
        width: 105,
        valueGetter: (params) => (params.row.payment?.paid ? params.row.payment.discount : null),
        renderCell: (params) => (
            <Cell value={params.value == null ? NA : `-${formatMoney(params.value)}`} dim={!params.value} />
        ),
    },
    {
        /**
         * What the family already owed, carried into this year — and the only column here that ADDS.
         *
         * Shown because without it the row does not add up for the families who had a debt: parent
         * 21605's fees are 2,250 with 910 paid, yet 1,800 is left, because 460 of last year came
         * with them. Of the 54 rows where the arithmetic looked wrong, 53 were this.
         *
         * Signed, so it cannot be mistaken for another deduction.
         */
        field: 'outstanding',
        headerName: 'Debt',
        width: 100,
        valueGetter: (params) => (params.row.payment?.paid ? params.row.payment.outstanding : null),
        renderCell: (params) => {
            const owed = Number(params.value) > 0;
            return (
                <Typography sx={{
                    fontSize: '13px',
                    fontWeight: owed ? 700 : 400,
                    color: params.value == null ? colour.faint : (owed ? colour.warnInk : colour.bodySoft),
                    whiteSpace: 'nowrap',
                }}>
                    {params.value == null ? NA : (owed ? `+${formatMoney(params.value)}` : formatMoney(0))}
                </Typography>
            );
        },
    },
    {
        /**
         * Credit is PART OF Paid, not another deduction.
         *
         * `paid_amount = AmountPaidFromBpoint + AmountPaidFromAICCredit` on all 323 paid rows on
         * production, so Credit and Card are what Paid is made of. Deducting Credit again after
         * Paid would take it off twice — which is why they sit before Paid and Paid carries the
         * weight.
         */
        field: 'credit',
        headerName: 'Credit',
        width: 100,
        valueGetter: (params) => (params.row.payment?.declined ? null : (params.row.payment?.fromCredit ?? null)),
        renderCell: (params) => (params.row.payment?.declined
            ? <Declined />
            : <Cell value={params.value == null ? NA : formatMoney(params.value)} dim />),
    },
    {
        // What actually reached the bank. "Paid" is the total and includes credit, which never goes
        // near BPoint — so without this column AR could not tell a $900 card payment from $900 of
        // credit, and the two reconcile against entirely different things.
        field: 'card',
        headerName: 'Card',
        width: 100,
        valueGetter: (params) => (params.row.payment?.declined ? null : (params.row.payment?.fromCard ?? null)),
        renderCell: (params) => (params.row.payment?.declined
            ? <Declined />
            : <Cell value={params.value == null ? NA : formatMoney(params.value)} />),
    },
    {
        field: 'paid',
        headerName: 'Paid',
        width: 105,
        valueGetter: (params) => ((params.row.payment?.paid && !params.row.payment?.declined)
            ? params.row.payment.paidAmount
            : null),
        renderCell: (params) => (params.row.payment?.declined
            ? <Declined />
            : (
                <Typography sx={{
                    fontSize: '13px',
                    fontWeight: params.value == null ? 400 : 700,
                    color: params.value == null ? colour.faint : colour.ink,
                    whiteSpace: 'nowrap',
                }}>
                    {params.value == null ? NA : formatMoney(params.value)}
                </Typography>
            )),
    },
    {
        /**
         * What is still owed — "Amount Remaining", shortened the same way.
         *
         * After Paid, because it is what Paid did not cover. Nonzero is the reading that matters,
         * so it gets the weight and the colour: these are the families AR chase. Zero is shown
         * rather than blanked, because "paid in full" is an answer and NA is not.
         */
        field: 'remaining',
        headerName: 'Left',
        width: 105,
        valueGetter: (params) => ((params.row.payment?.paid && !params.row.payment?.declined)
            ? params.row.payment.remaining
            : null),
        renderCell: (params) => {
            // remaining_amount was written as though the declined attempt had gone through, so it
            // understates what is owed. Showing it would be the same lie as showing Paid.
            if (params.row.payment?.declined) return <Declined />;
            const owed = Number(params.value) > 0;
            return (
                <Typography sx={{
                    fontSize: '13px',
                    fontWeight: owed ? 700 : 400,
                    color: params.value == null ? colour.faint : (owed ? colour.ink : colour.bodySoft),
                    whiteSpace: 'nowrap',
                }}>
                    {params.value == null ? NA : formatMoney(params.value)}
                </Typography>
            );
        },
    },
    {
        // What the family asked for, not what has been arranged. The payment page tells them in so
        // many words that choosing one here does not activate it and that AR must set it up — so
        // this is a request sitting in the data that nobody could see until now.
        field: 'remaining_method',
        headerName: 'Pay rest by',
        flex: 0.5,
        minWidth: 115,
        valueGetter: (params) => params.row.payment?.remainingMethod || '',
        renderCell: (params) => (
            <Cell value={!params.value || params.value === 'Not Applicable' ? NA : params.value} dim={!params.value} />
        ),
    },
    // The bank references, as a group: when it was paid, when BPoint settled it, the receipt. AR
    // read these against BPoint's own reports rather than against the amounts above.
    { ...plain('transaction_at', 'Paid at', undefined, (row) => transactionLabel(row)), flex: 0.5, minWidth: 140 },
    {
        ...plain('settlement_date', 'Settled', 120, (row) => settlementLabel(row.payment?.settlementDate)),
        ...filtered('settled', 'Settled'),
    },
    plain('receipt_number', 'Receipt', 110, (row) => text(row.payment?.receiptNumber)),
    // The same number for every row at a campus, so it is reference rather than reading.
    ...(compact ? [] : [plain('merchant_number', 'Merchant', 115, (row) => text(row.payment?.merchantNumber))]),
    // The newest comment from AR or the enrolment team; every comment goes in the hidden Comments
    // column, for the export. The row itself opens the family (ApproverScreen's onRowClick), which is
    // where the card images, the fee breakdown and the full comments are.
    latestCommentColumn(),
    allCommentsColumn(),
    ];
};

export const ACCOUNTS_NA = NA;
